// 組織設定 › IT 監控 › 管理員 passkey（`/org/kmgr/keys`；SPEC #kinio-kms-onboarding「權杖與管理員清單」、#kinio-kms-api keysets、D29、D35；k1-wbs M3-05）。
// 清單 = 每台電腦的管理程式認得的管理員簽章 passkey。新增要清單上的一把在簽章網址簽（新清單的雜湊由簽章頁自己算）；
// 移除不用簽，但組織之前建的佈署權杖會全部失效（先 dry_run 列出來給人確認）。目前清單是空的時新增 = 重新錨定（之後建的權杖才帶）。
// 重新錨定之後已報到的電腦照規則拒收新清單：kmgr 停止重送、列在 reinstall，這頁列出來請管理員用新權杖重裝（SPEC #kinio-kms-cmd「keyset 變更鏈」）。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useApp } from '../../lib/AppContext';
import { useScope } from '../../lib/scope';
import { listPasskeys } from '../../lib/session';
import type { MyOrg } from '../../lib/planeApi';
import { SkeletonLines } from '../../components/Skeleton';
import { kmgrApi } from './api';
import { useKmgrEnv } from './hooks';
import { errText, NS } from './parts';
import { requestSignature } from './signWindow';
import type { AdminKey, KeysetAssertion, KeysetChangeResult, KeysetReinstall } from './types';

const short = (id: string) => id.slice(0, 8);

export function AdminKeys({ org }: { org: MyOrg }) {
  const { t } = useTranslation(NS);
  const { config } = useApp();
  const { me } = useKmgrEnv();
  const qc = useQueryClient();
  const ks = useQuery({ queryKey: ['kmgr', 'keysets', org.org_id], queryFn: () => kmgrApi.keysets(org.api, org.org_id), retry: 0 });
  const pk = useQuery({ queryKey: ['passkeys'], queryFn: () => listPasskeys(), retry: 0 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState<{ key: AdminKey; reason: 'device_replaced' | 'lost'; dry: KeysetChangeResult } | null>(null);
  if (ks.isLoading) return <SkeletonLines n={3} label={t('common.loading')} />;
  if (ks.isError || !ks.data) return <p className="card muted">{t('keys.unavailable', { detail: errText(t, ks.error) })}</p>;
  const cur = ks.data.current;
  const mineIDs = new Set((pk.data ?? []).filter((p) => p.kms_sign).map((p) => p.id));
  const myKeys = cur.keys.filter((k) => k.user_id === me && mineIDs.has(k.cred_id));
  const empty = cur.keys.length === 0;
  const who = (u: string) => (u === me ? t('keys.you') : u);
  const refresh = () => qc.invalidateQueries({ queryKey: ['kmgr', 'keysets', org.org_id] });

  const add = async (k: AdminKey) => {
    setBusy(true);
    setErr(null);
    try {
      const next = { org_id: cur.org_id, version: cur.version + 1, keys: [...cur.keys, k] };
      if (empty) {
        await kmgrApi.changeKeyset(org.api, org.org_id, { version: next.version, change: 'add', keys: next.keys });
      } else {
        if (!config.sign_url) throw new Error(t('wizard.passkey_no_sign'));
        const a = await requestSignature<KeysetAssertion>(config.sign_url, 'keyset', {
          keyset: next,
          previous: { org_id: cur.org_id, version: cur.version, keys: cur.keys },
          cred_ids: myKeys.map((m) => m.cred_id),
        });
        await kmgrApi.changeKeyset(org.api, org.org_id, { version: next.version, change: 'add', keys: next.keys, signed_by: a.cred_id, assertion: a });
      }
      await refresh();
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  };

  const prepareRemove = async (k: AdminKey, reason: 'device_replaced' | 'lost') => {
    setBusy(true);
    setErr(null);
    try {
      const keys = cur.keys.filter((x) => x.cred_id !== k.cred_id);
      const dry = await kmgrApi.changeKeyset(org.api, org.org_id, { version: cur.version + 1, change: 'remove', keys, remove_reason: reason }, true);
      setPending({ key: k, reason, dry });
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  };

  const confirmRemove = async () => {
    if (!pending) return;
    setBusy(true);
    setErr(null);
    try {
      const keys = cur.keys.filter((x) => x.cred_id !== pending.key.cred_id);
      await kmgrApi.changeKeyset(org.api, org.org_id, { version: cur.version + 1, change: 'remove', keys, remove_reason: pending.reason });
      setPending(null);
      await refresh();
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="km-admin-keys">
      <p className="muted km-sub">{t('keys.intro')}</p>
      <section className="card" style={{ marginBottom: 'var(--space-3)' }}>
        <h2 style={{ fontSize: 15, marginTop: 0 }}>{t('keys.current', { v: cur.version })}</h2>
        {empty ? (
          <p className="muted">{t('keys.empty')}</p>
        ) : (
          <ul className="plain-list">
            {cur.keys.map((k) => (
              <li key={k.cred_id}>
                {who(k.user_id)} · passkey {short(k.cred_id)}
                {ks.data.stale.includes(k.cred_id) && <span className="muted"> · {t('keys.stale')}</span>}{' '}
                <button className="btn btn-sm" disabled={busy} onClick={() => void prepareRemove(k, 'device_replaced')}>
                  {t('keys.remove_replaced')}
                </button>{' '}
                <button className="btn btn-sm" disabled={busy} onClick={() => void prepareRemove(k, 'lost')}>
                  {t('keys.remove_lost')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      {pending && (
        <section className="card" role="alertdialog" aria-labelledby="km-keys-confirm" style={{ marginBottom: 'var(--space-3)' }}>
          <h2 id="km-keys-confirm" style={{ fontSize: 15, marginTop: 0 }}>
            {t('keys.confirm_title', { who: who(pending.key.user_id), id: short(pending.key.cred_id) })}
          </h2>
          <p>{pending.dry.revoked_tokens.length ? t('keys.confirm_tokens', { n: pending.dry.revoked_tokens.length }) : t('keys.confirm_no_tokens')}</p>
          {pending.dry.revoked_tokens.length > 0 && (
            <ul className="plain-list">
              {pending.dry.revoked_tokens.map((x) => (
                <li key={x.token_id}>{t('keys.token_line', { name: x.name, n: x.remaining })}</li>
              ))}
            </ul>
          )}
          <button className="btn btn-primary" disabled={busy} onClick={() => void confirmRemove()}>
            {t('keys.confirm_remove', { n: pending.dry.revoked_tokens.length })}
          </button>{' '}
          <button className="btn" disabled={busy} onClick={() => setPending(null)}>
            {t('common.cancel')}
          </button>
        </section>
      )}
      {(ks.data.reinstall?.length ?? 0) > 0 && <ReinstallList list={ks.data.reinstall!} />}
      <section className="card">
        <h2 style={{ fontSize: 15, marginTop: 0 }}>{t('keys.candidates')}</h2>
        {ks.data.candidates.length === 0 ? (
          <p className="muted">{t('keys.no_candidates')}</p>
        ) : (
          <ul className="plain-list">
            {ks.data.candidates.map((k) => (
              <li key={k.cred_id}>
                {who(k.user_id)} · passkey {short(k.cred_id)}{' '}
                <button className="btn btn-sm" disabled={busy || (!empty && myKeys.length === 0)} onClick={() => void add(k)}>
                  {empty ? t('keys.add_anchor') : t('keys.add_sign')}
                </button>
              </li>
            ))}
          </ul>
        )}
        {!empty && myKeys.length === 0 && <p className="muted km-sub">{t('keys.need_listed')}</p>}
      </section>
      {err && (
        <p className="err" role="alert">
          {err}
        </p>
      )}
    </div>
  );
}

/** 管理員清單送不到的電腦：一句原因 + 每台連到主機頁 +〔建佈署權杖〕（那個家戶的監控設定 › 佈署權杖）。 */
function ReinstallList({ list }: { list: KeysetReinstall[] }) {
  const { t } = useTranslation(NS);
  const { homes: scopeHomes } = useScope();
  const homes = [...new Set(list.map((x) => x.home_id))];
  const homeName = (id: string) => scopeHomes.find((h) => h.home_id === id)?.display_name ?? id;
  return (
    <section className="card km-bar-card" data-testid="km-keys-reinstall" style={{ marginBottom: 'var(--space-3)', borderLeft: '4px solid var(--warn, #b45309)' }}>
      <h2 style={{ fontSize: 15, marginTop: 0 }}>{t('keys.reinstall_title', { n: list.length })}</h2>
      <p className="muted km-sub">{t('keys.reinstall_body')}</p>
      <ul className="plain-list">
        {list.map((x) => (
          <li key={x.device_ref} data-device={x.device_ref}>
            <Link to={`/kmgr/hosts/${encodeURIComponent(x.home_id)}/${encodeURIComponent(x.device_ref)}`}>{x.name || x.device_ref}</Link>
            <span className="muted"> · {homes.length > 1 ? homeName(x.home_id) + ' · ' : ''}{x.reason === 'reanchor_required' ? t('keys.reinstall_reanchor', { v: x.keyset_version }) : t('keys.reinstall_rejected', { code: x.reason })}</span>
          </li>
        ))}
      </ul>
      <div className="toolbar wrap" style={{ marginBottom: 0 }}>
        {homes.slice(0, 5).map((h) => (
          <Link key={h} className="btn btn-sm" to={`/sites/kmgr/${encodeURIComponent(h)}/tokens/new`}>
            {homes.length > 1 ? t('keys.reinstall_token_home', { home: homeName(h) }) : t('keys.reinstall_token')}
          </Link>
        ))}
      </div>
    </section>
  );
}
